import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { supabase } from '@/config/supabase';
import type { Certificate, ExamDifficulty } from '@/types/exam';

export const certificateService = {
  /**
   * Generate PDF certificate HTML
   */
  generateCertificateHTML(certificate: Certificate): string {
    const difficultyBadgeColor = {
      beginner: '#fbbf24', // amber
      intermediate: '#60a5fa', // blue
      expert: '#ec4899', // pink
    }[certificate.roadmap_difficulty];

    return `
      <div id="certificate-content" style="
        width: 1200px;
        height: 850px;
        background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
        padding: 60px;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        position: relative;
        border: 3px solid #dc2626;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        box-sizing: border-box;
      ">
        <!-- Top decorative bar -->
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 8px;
          background: linear-gradient(90deg, #dc2626 0%, #ea580c 50%, #e11d48 100%);
        "></div>

        <!-- Header with logo area -->
        <div style="text-align: center; margin-bottom: 30px;">
          <div style="font-size: 14px; color: #666; letter-spacing: 2px; font-weight: 600;">
            NANOLEŽ LEARNING PLATFORM
          </div>
        </div>

        <!-- Main Certificate Content -->
        <div style="text-align: center; flex: 1; display: flex; flex-direction: column; justify-content: center;">
          <!-- Title -->
          <h1 style="
            font-size: 56px;
            color: #1f2937;
            margin: 0 0 20px 0;
            font-weight: 700;
            letter-spacing: -1px;
          ">Certificate of Achievement</h1>

          <!-- Subtitle -->
          <p style="
            font-size: 20px;
            color: #6b7280;
            margin: 0 0 50px 0;
            font-style: italic;
          ">Successfully Completed Assessment</p>

          <!-- Main text -->
          <p style="
            font-size: 18px;
            color: #374151;
            margin: 0 0 15px 0;
            line-height: 1.8;
          ">
            This is proudly presented to
          </p>

          <!-- User name -->
          <h2 style="
            font-size: 48px;
            color: #dc2626;
            margin: 15px 0 30px 0;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
          ">${certificate.user_name}</h2>

          <!-- Achievement text -->
          <div style="
            background: rgba(220, 38, 38, 0.05);
            padding: 25px 40px;
            border-radius: 8px;
            margin: 30px 0;
          ">
            <p style="
              font-size: 18px;
              color: #374151;
              margin: 0 0 12px 0;
              line-height: 1.8;
            ">
              For successfully completing the
            </p>
            <p style="
              font-size: 32px;
              color: #1f2937;
              margin: 12px 0;
              font-weight: 700;
            ">
              ${certificate.roadmap_name}
            </p>
            <p style="
              font-size: 18px;
              color: #374151;
              margin: 12px 0 0 0;
              line-height: 1.8;
            ">
              Assessment at <span style="
                background: ${difficultyBadgeColor};
                color: white;
                padding: 6px 14px;
                border-radius: 20px;
                font-weight: 600;
                text-transform: uppercase;
                font-size: 14px;
              ">${certificate.roadmap_difficulty}</span> Level
            </p>
          </div>

          <!-- Score -->
          <p style="
            font-size: 20px;
            color: #1f2937;
            margin: 30px 0 0 0;
            font-weight: 600;
          ">
            Achieved Score: <span style="
              color: #dc2626;
              font-size: 28px;
              font-weight: 700;
            ">${certificate.score_percentage}%</span>
          </p>
        </div>

        <!-- Bottom section with date and credential info -->
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          padding-top: 40px;
          border-top: 2px solid #e5e7eb;
        ">
          <!-- Date -->
          <div>
            <p style="
              font-size: 13px;
              color: #6b7280;
              margin: 0 0 8px 0;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: 600;
            ">Date of Issue</p>
            <p style="
              font-size: 16px;
              color: #1f2937;
              margin: 0;
              font-weight: 600;
            ">${new Date(certificate.issued_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}</p>
          </div>

          <!-- Credential code -->
          <div style="text-align: center;">
            <p style="
              font-size: 12px;
              color: #6b7280;
              margin: 0 0 8px 0;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: 600;
            ">Credential ID</p>
            <p style="
              font-size: 14px;
              color: #1f2937;
              margin: 0;
              font-family: 'Courier New', monospace;
              font-weight: 700;
              letter-spacing: 1px;
            ">${certificate.credential_code || 'CERT-' + Math.random().toString(36).substring(7).toUpperCase()}</p>
          </div>

          <!-- Verification text -->
          <div style="text-align: right;">
            <p style="
              font-size: 12px;
              color: #6b7280;
              margin: 0 0 8px 0;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: 600;
            ">Verified Certificate</p>
            <p style="
              font-size: 13px;
              color: #10b981;
              margin: 0;
              font-weight: 600;
            ">✓ Authentic</p>
          </div>
        </div>

        <!-- Bottom decorative bar -->
        <div style="
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 8px;
          background: linear-gradient(90deg, #dc2626 0%, #ea580c 50%, #e11d48 100%);
        "></div>
      </div>
    `;
  },

  /**
   * Generate PDF from certificate data
   */
  async generateCertificatePDF(certificate: Certificate): Promise<Blob> {
    try {
      // Create temporary container
      const container = document.createElement('div');
      container.innerHTML = this.generateCertificateHTML(certificate);
      container.style.position = 'absolute';
      container.style.left = '-10000px';
      document.body.appendChild(container);

      // Convert to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      // Clean up
      document.body.removeChild(container);

      return pdf.output('blob');
    } catch (error) {
      console.error('Error generating certificate PDF:', error);
      throw error;
    }
  },

  /**
   * Upload certificate PDF to Supabase Storage
   */
  async uploadCertificatePDF(
    pdf: Blob,
    certificate: Certificate
  ): Promise<string> {
    try {
      const fileName = `certificates/${certificate.user_id}/${certificate.id}.pdf`;

      const { data, error } = await supabase.storage
        .from('certificates')
        .upload(fileName, pdf, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('certificates')
        .getPublicUrl(fileName);

      return publicUrlData?.publicUrl || '';
    } catch (error) {
      console.error('Error uploading certificate PDF:', error);
      throw error;
    }
  },

  /**
   * Generate QR code for credential
   */
  async generateCredentialQRCode(credentialCode: string): Promise<string> {
    try {
      const credentialUrl = `${window.location.origin}/verify-credential?code=${credentialCode}`;
      const qrCode = await QRCode.toDataURL(credentialUrl, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });

      return qrCode;
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  },

  /**
   * Save certificate to database and storage
   */
  async saveCertificate(certificate: Certificate): Promise<void> {
    try {
      // Generate PDF
      const pdfBlob = await this.generateCertificatePDF(certificate);

      // Upload to storage
      const certificateUrl = await this.uploadCertificatePDF(pdfBlob, certificate);

      // Update certificate in database with URL
      const { error } = await supabase
        .from('certificates')
        .update({
          certificate_url: certificateUrl,
        })
        .eq('id', certificate.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving certificate:', error);
      throw error;
    }
  },

  /**
   * Verify credential by code
   */
  async verifyCredential(credentialCode: string): Promise<Certificate | null> {
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('credential_code', credentialCode)
        .single();

      if (error || !data) {
        return null;
      }

      return data as Certificate;
    } catch (error) {
      console.error('Error verifying credential:', error);
      return null;
    }
  },

  /**
   * Download certificate PDF
   */
  async downloadCertificate(certificateUrl: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(certificateUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      throw error;
    }
  },
};
